import React, { useEffect, useState } from "react";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import "./parent.css";

function ParentProfile({ parentData, onProfileUpdated, onLogout }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    username: "",
    password: "",
    confirmPassword: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (!parentData) return;
    setForm((prev) => ({
      ...prev,
      name: parentData.name || "",
      phone: parentData.phone || "",
      username: parentData.username || "",
      password: "",
      confirmPassword: "",
    }));
  }, [parentData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
    setSuccess("");
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");

    const username = form.username.trim();
    if (username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }

    if (form.password || form.confirmPassword) {
      if (form.password.length < 6) {
        setError("Password must be at least 6 characters.");
        return;
      }
      if (form.password !== form.confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
    }

    const payload = {
      name: form.name.trim() || null,
      phone: form.phone.trim() || null,
      username,
    };
    if (form.password) {
      payload.password = form.password;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/parents/me`, {
        method: "PUT",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        handleUnauthorized(onLogout);
        return;
      }

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = data.detail;
        const message = Array.isArray(detail)
          ? detail.map((d) => d.msg || d).join(", ")
          : detail || "Failed to update profile";
        throw new Error(message);
      }

      localStorage.setItem("als_parent_name", data.name || "");
      setForm((prev) => ({
        ...prev,
        name: data.name || "",
        phone: data.phone || "",
        username: data.username || "",
        password: "",
        confirmPassword: "",
      }));
      setSuccess("Profile updated. Use your new username and password on next login.");
      setIsEditing(false);
      onProfileUpdated?.(data);
    } catch (err) {
      setError(err.message || "Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  if (!parentData) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading profile…</p>
      </div>
    );
  }

  const initials = (form.name || parentData.username || "P")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="pp-page pp-animate-in">
      <header className="pp-hero pp-hero--profile">
        <div className="pp-profile-hero-main">
          <div className="pp-profile-avatar" aria-hidden="true">
            {initials}
          </div>
          <div>
            <p className="pp-kicker">Account settings</p>
            <h1>My Profile</h1>
            <p className="pp-sub">
              Update your login username, password, and contact details.
            </p>
          </div>
        </div>
        <div className="pp-profile-actions">
          {!isEditing ? (
            <button type="button" className="pp-btn pp-btn--primary" onClick={() => setIsEditing(true)}>
              Edit profile
            </button>
          ) : (
            <>
              <button
                type="button"
                className="pp-btn pp-btn--ghost-light"
                onClick={() => {
                  setIsEditing(false);
                  setError("");
                  setForm((prev) => ({
                    ...prev,
                    name: parentData.name || "",
                    phone: parentData.phone || "",
                    username: parentData.username || "",
                    password: "",
                    confirmPassword: "",
                  }));
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pp-btn pp-btn--gold"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </>
          )}
        </div>
      </header>

      {error && <div className="pp-alert pp-alert--error">{error}</div>}
      {success && <div className="pp-alert pp-alert--success">{success}</div>}

      <article className="pp-panel pp-profile-panel">
        <div className="pp-profile-section-head">
          <h2>Personal &amp; login details</h2>
          <p>CNIC is locked. Username and password replace previous credentials when saved.</p>
        </div>

        <div className="pp-form-grid">
          <label className="pp-field">
            <span>Full name</span>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="Parent / guardian name"
            />
          </label>
          <label className="pp-field">
            <span>Phone</span>
            <input
              name="phone"
              value={form.phone}
              onChange={handleChange}
              disabled={!isEditing}
              placeholder="Contact number"
            />
          </label>
          <label className="pp-field">
            <span>CNIC</span>
            <input value={parentData.cnic || ""} disabled readOnly />
          </label>
          <label className="pp-field">
            <span>Username</span>
            <input
              name="username"
              value={form.username}
              onChange={handleChange}
              disabled={!isEditing}
              autoComplete="username"
            />
          </label>
          {isEditing && (
            <>
              <label className="pp-field pp-field--password">
                <span>New password</span>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Leave blank to keep current"
                  autoComplete="new-password"
                />
              </label>
              <label className="pp-field pp-field--password">
                <span>Confirm password</span>
                <input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
              </label>
            </>
          )}
        </div>

        <div className="pp-profile-note">
          <strong>Security note</strong>
          <p>
            Changing username or password replaces your previous login credentials immediately.
            Siblings linked to the same CNIC share this account.
          </p>
        </div>
      </article>
    </div>
  );
}

export default ParentProfile;
